import { z } from 'zod';

/**
 * Feature flags (T-152) — gate the untested Phase-2 features so prod can be
 * deployed with the whole codebase but only well-tested features exposed.
 *
 * Stored one row per flag in `app_settings` under the key `feature.<name>`
 * with value 'true' | 'false'. **A missing key means OFF** — a fresh prod
 * deploy therefore exposes none of these features until an admin flips them
 * on in Settings (no redeploy needed).
 *
 * NOT flagged: the referral feature (already tested + wanted everywhere) and
 * the T-132 e-sign incident batch (those are fixes, not new surface). Anything
 * absent from this registry is simply always-on.
 */
export const FEATURE_FLAG_DEFS = [
  {
    key: 'o365_mailbox',
    label: 'O365 mailbox provisioning',
    description:
      'Create the uIT mailbox via Microsoft Graph during onboarding, manual mailbox removal, and deprovision on cancel/expire (T-114 / T-140 / T-141).',
  },
  {
    key: 'rcf',
    label: 'Resource Change Form (RCF)',
    description:
      'RCF generation, the review-and-send screen, and the send triggers to Cigna (T-115 / T-142 / T-143 / T-144).',
  },
  {
    key: 'qb_bill_push',
    label: 'Push billing to QuickBooks',
    description:
      'The "Push to QuickBooks" action on a billing run, which creates the vendor bills in QuickBooks (T-066). Off = Eric keeps uploading the bill file manually.',
  },
  {
    key: 'checkr',
    label: 'Checkr background checks',
    description:
      'Background-check integration (T-116): order + status surface via Checkr Hosted Flow. Keep OFF in prod until T-176 (FCRA adverse-action) is in.',
  },
] as const;

export const FEATURE_FLAG_KEYS = FEATURE_FLAG_DEFS.map((d) => d.key) as readonly FeatureFlag[];

export type FeatureFlag = (typeof FEATURE_FLAG_DEFS)[number]['key'];

export type FeatureFlags = Record<FeatureFlag, boolean>;

/** The safe default: every flag OFF. A fresh deploy exposes no gated feature. */
export const FEATURE_FLAGS_ALL_OFF: FeatureFlags = Object.fromEntries(
  FEATURE_FLAG_KEYS.map((k) => [k, false]),
) as FeatureFlags;

export function isFeatureFlag(value: string): value is FeatureFlag {
  return (FEATURE_FLAG_KEYS as readonly string[]).includes(value);
}

/** The `app_settings` key a flag is stored under. */
export function featureFlagSettingKey(flag: FeatureFlag): string {
  return `feature.${flag}`;
}

// GET /api/feature-flags — public; every key present, missing rows coerced to false.
export const FeatureFlagsResponse = z.object(
  Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, z.boolean()])) as Record<
    FeatureFlag,
    z.ZodBoolean
  >,
);
export type FeatureFlagsResponse = z.infer<typeof FeatureFlagsResponse>;

// PATCH /api/settings/feature-flags — admin; a partial set of flags to update.
export const FeatureFlagsUpdateInput = z
  .object(
    Object.fromEntries(FEATURE_FLAG_KEYS.map((k) => [k, z.boolean().optional()])) as Record<
      FeatureFlag,
      z.ZodOptional<z.ZodBoolean>
    >,
  )
  .strict();
export type FeatureFlagsUpdateInput = z.infer<typeof FeatureFlagsUpdateInput>;
